import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, onMounted, nextTick, getCurrentInstance } from 'vue';
import { navigationService } from 'vue-spatialnavigation';
import { RailResponse, SeriesItem } from '@/types/cms';
import { useRailNavigation } from '@/composables/useRailNavigation';
import { focusElementById } from '@/helpers/focus';
import CarouselCell from '@/components/cells/CarouselCell.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'CarouselRail',
  props: {
    rail: null,
    railIndex: null,
    railDidNavigateDown: null
  },
  setup(__props: any) {

const props = __props as {
  rail: RailResponse;
  railIndex: number;
  railDidNavigateDown?: (railIndex: number) => void;
};





const { containerId, itemId, handleUp, handleDown, handleLeftFallback } =
  useRailNavigation(props);

const instance = getCurrentInstance();
const lazyLoad = instance?.proxy?.$lazyLoad;

const currentIndex = ref(0);
const stride = ref(0);

const items = computed(() =>
  props.rail.items
    .filter((item): item is SeriesItem => item.type === 'SERIES')
    .map((item) => item.series)
);

/**
 * Distance from one card's left edge to the next.
 *
 * Measured off the DOM rather than recomputed here, so the card width and the
 * gap stay defined once each in SCSS. `offsetLeft` is a layout value, so
 * neither the track's own transform nor the design canvas's affects it, and
 * this stays correct whatever the row's current position.
 */
const measureStride = () => {
  const first = document.getElementById(itemId(0));
  const second = document.getElementById(itemId(1));
  if (first && second) stride.value = second.offsetLeft - first.offsetLeft;
};

// The first card is centred by its own margin, so index 0 needs no shift and
// every other index is that many strides to the left.
const trackStyle = computed(() => ({
  transform: `translateX(-${currentIndex.value * stride.value}px)`,
}));

/**
 * Keeps the viewport's own scroll offset at zero.
 *
 * Nothing should ever scroll it — position is the track's transform — but the
 * row still overflows it in layout terms, which leaves it programmatically
 * scrollable. Focusing a card that is still mid-slide makes the browser reveal
 * it by scrolling the viewport, stacking a scroll offset on top of the
 * transform. Pinning it back is more general than predicting every caller;
 * `focus({ preventScroll: true })` would cover the focus case alone but needs
 * Chrome 64, above our Tizen 4 floor.
 */
const pinScroll = () => {
  const viewport = document.getElementById(containerId.value);
  if (viewport && viewport.scrollLeft !== 0) viewport.scrollLeft = 0;
};

const focusCard = (index: number) => {
  const targetId = itemId(index);
  if (!navigationService.getFocusElementById(targetId)) return;

  currentIndex.value = index;

  //Call helper with no scrolling
  focusElementById(targetId);

  // Still required below that, where `preventScroll` is ignored: in the same
  // task as the focus that may have caused a reveal-scroll, so the displaced
  // frame is never painted.
  pinScroll();
};

const handleLeft = (index: number) => {
  if (index === 0) {
    handleLeftFallback();
    return;
  }
  focusCard(index - 1);
};

const handleRight = (index: number) => {
  if (index >= items.value.length - 1) return;
  focusCard(index + 1);
};


const onCardFocused = (index: number) => {
  if (index === 0 && currentIndex.value !== 0) {
    nextTick(() => focusCard(currentIndex.value));
    return;
  }

  currentIndex.value = index;
};

const scanForLazyImages = () => {
  setTimeout(() => {
    const viewport = document.getElementById(containerId.value);
    if (viewport) lazyLoad?.(viewport);
  }, 200);
};

onMounted(() => {
  scanForLazyImages();
  measureStride();
});

//Rails are keyed on ID, so a rail whose items change is updated rather than remounted
watch(() => items.value.map((series) => series.slug).join(), () => {
  scanForLazyImages();
  nextTick(measureStride);

  // Polling can shrink the rail out from under us; a currentIndex past the end
  // would dim every card and light no dot.
  const lastIndex = items.value.length - 1;
  if (currentIndex.value > lastIndex) {
    currentIndex.value = Math.max(0, lastIndex);
  }
});

return { __sfc: true,props, containerId, itemId, handleUp, handleDown, handleLeftFallback, instance, lazyLoad, currentIndex, stride, items, measureStride, trackStyle, pinScroll, focusCard, handleLeft, handleRight, onCardFocused, scanForLazyImages, CarouselCell }
}

})